import { Plus, Trash2 } from 'lucide-react';
import { EvaluationProviderConfig as EvaluationProviderConfigType } from '../types/evaluation';
import { EvaluationProviderEditor } from './EvaluationProviderEditor';

interface EvaluationProviderConfigProps {
  configs: EvaluationProviderConfigType[];
  onConfigsChange: (configs: EvaluationProviderConfigType[]) => void;
  disabled?: boolean;
  validationErrors?: Record<string, string>;
  mode?: 'single' | 'multi';
}

export const EvaluationProviderConfig = ({
  configs,
  onConfigsChange,
  disabled = false,
  validationErrors = {},
  mode = 'multi'
}: EvaluationProviderConfigProps) => {
  const handleAddProvider = () => {
    const newConfig: EvaluationProviderConfigType = {
      id: crypto.randomUUID(),
      name: '',
      maxOpinions: 5,
      maxPassagesPerDocument: 3,
      useReducer: false
    };
    onConfigsChange([...configs, newConfig]);
  };

  const handleRemoveProvider = (id: string) => {
    if (configs.length <= 2 && mode === 'multi') {
      alert('At least 2 provider configurations are required');
      return;
    }
    const newConfigs = configs.filter(c => c.id !== id);
    onConfigsChange(newConfigs);
  };

  const handleUpdateConfig = (id: string, updatedConfig: EvaluationProviderConfigType) => {
    const newConfigs = configs.map(c => c.id === id ? updatedConfig : c);
    onConfigsChange(newConfigs);
  };

  const handleCopyFromPrevious = (currentIndex: number) => {
    if (currentIndex === 0) return; // No previous config for first provider
    
    const previousConfig = configs[currentIndex - 1];
    const currentConfig = configs[currentIndex];
    
    // Copy all settings from previous config except id and name
    const copiedConfig: EvaluationProviderConfigType = {
      ...previousConfig,
      id: currentConfig.id, // Keep current id
      name: currentConfig.name // Keep current name (user can change it)
    };
    
    handleUpdateConfig(currentConfig.id, copiedConfig);
  };

  return (
    <div data-component="EvaluationProviderConfig">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Provider Configurations</h3>
        {mode === 'multi' && (
          <button
            type="button"
            onClick={handleAddProvider}
            disabled={disabled}
            className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Add Provider
          </button>
        )}
      </div>

      <div className="space-y-4">
        {configs.map((config, index) => (
          <div key={config.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <h4 className="font-medium text-gray-900">Provider {index + 1}</h4>
              {mode === 'multi' && configs.length > 2 && (
                <button
                  type="button"
                  onClick={() => handleRemoveProvider(config.id)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <EvaluationProviderEditor
              value={config}
              onChange={(updated) => handleUpdateConfig(config.id, updated)}
              disabled={disabled}
              validationErrors={{
                name: validationErrors[`provider_${index}_name`]
              }}
              previousConfig={index > 0 ? configs[index - 1] : undefined}
              previousProviderIndex={index > 0 ? index - 1 : undefined}
              onCopyFromPrevious={index > 0 ? () => handleCopyFromPrevious(index) : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
